import { useState, useCallback } from 'react';
import { useCustomers, type Musteri, type MusteriFormData, type IlkServisFormData, type FilterType } from './hooks/useCustomers';
import CustomerModal from './components/CustomerModal';
import CustomerDetailModal from './components/CustomerDetailModal';
import { telefonWhatsApp } from '@/pages/bakim-takip/utils/bakimHesapla';

const durumConfig: Record<string, { label: string; cls: string; dot: string }> = {
  yesil: { label: 'Bakım Süresi Uygun', cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  sari: { label: 'Bakım Zamanı Geldi', cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  turuncu: { label: 'Bakım Gecikiyor', cls: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  kirmizi: { label: 'Kritik Bakım Gecikmesi', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  yok: { label: 'Bakım Yok', cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
};

export default function MusterilerPage() {
  const {
    paginated,
    totalPages,
    page,
    setPage,
    totalCount,
    loading,
    error,
    search,
    setSearch,
    filter,
    setFilter,
    durumCounts,
    saving,
    saveError,
    saveSuccess,
    saveCustomer,
    createIlkServisKaydi,
    deleteCustomer,
    clearMessages,
    refetch,
  } = useCustomers();

  const [modalOpen, setModalOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Musteri | null>(null);
  const [detailCustomer, setDetailCustomer] = useState<Musteri | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleSave = useCallback(async (data: MusteriFormData, servisData: IlkServisFormData) => {
    const result = await saveCustomer(data, editCustomer?.id);
    if (!result || !result.ok) return;

    if (result.newId && servisData.islem_kaydi_olustur) {
      const servisSonuc = await createIlkServisKaydi(result.newId, servisData);
      if (!servisSonuc.ok) {
        setModalOpen(false);
        setEditCustomer(null);
        clearMessages();
        setTimeout(() => {
          alert('Müşteri oluşturuldu ancak ilk servis kaydı oluşturulamadı. Servis kaydını müşteri detayından tekrar oluşturabilirsiniz.');
        }, 300);
        return;
      }
    }

    setModalOpen(false);
    setEditCustomer(null);
    setTimeout(() => clearMessages(), 3000);
  }, [saveCustomer, createIlkServisKaydi, editCustomer, clearMessages]);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    setDeleting(true);
    const ok = await deleteCustomer(deleteId);
    setDeleting(false);
    if (ok) {
      setDeleteId(null);
      setTimeout(() => clearMessages(), 3000);
    }
  }, [deleteId, deleteCustomer, clearMessages]);

  const filterBtns: { key: FilterType; label: string; count: number }[] = [
    { key: 'hepsi', label: 'Tümü', count: durumCounts.hepsi },
    { key: 'yesil', label: 'Uygun', count: durumCounts.yesil },
    { key: 'sari', label: 'Zamanı Geldi', count: durumCounts.sari },
    { key: 'turuncu', label: 'Gecikiyor', count: durumCounts.turuncu },
    { key: 'kirmizi', label: 'Kritik', count: durumCounts.kirmizi },
  ];

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-500 text-2xl"></i>
        </div>
        <p className="text-gray-700 font-semibold text-base mb-1">{error}</p>
        <button onClick={refetch} className="mt-4 px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line mr-2"></i>Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {saveSuccess && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm font-medium">
          <i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>
          {saveSuccess}
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm font-medium">
          <i className="ri-close-circle-line text-red-500 text-lg"></i>
          {saveError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-sky-400 bg-white"
              placeholder="Müşteri ara..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {filterBtns.map(b => (
              <button key={b.key} onClick={() => setFilter(b.key)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap
                  ${filter === b.key ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
                {b.label} ({b.count})
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{totalCount} müşteri</span>
          <button onClick={() => { setEditCustomer(null); setModalOpen(true); clearMessages(); }}
            className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-user-add-line"></i>
            Yeni Müşteri
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-gray-400">Müşteriler yükleniyor...</p>
        </div>
      ) : paginated.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 flex flex-col items-center justify-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-user-search-line text-gray-400 text-2xl"></i>
          </div>
          <p className="text-gray-700 font-semibold text-base mb-1">
            {search || filter !== 'hepsi' ? 'Seçilen filtrelere uygun kayıt bulunamadı.' : 'Henüz müşteri kaydı bulunmuyor.'}
          </p>
          {!search && filter === 'hepsi' && (
            <button onClick={() => { setEditCustomer(null); setModalOpen(true); }}
              className="mt-3 px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-user-add-line mr-2"></i>İlk Müşteriyi Ekle
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Müşteri</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">İlçe</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Cihaz</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Son Bakım</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bakım Durumu</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">Harcama</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginated.map(c => {
                    const d = durumConfig[c.bakim_durumu] || durumConfig.yok;
                    const sonBakimText = c.son_bakim_tarihi
                      ? new Date(c.son_bakim_tarihi).toLocaleDateString('tr-TR')
                      : '-';
                    const sonrakiBakimText = c.bir_sonraki_bakim_tarihi
                      ? new Date(c.bir_sonraki_bakim_tarihi).toLocaleDateString('tr-TR')
                      : '-';
                    return (
                      <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-semibold text-gray-800">{c.ad} {c.soyad}</p>
                          <p className="text-xs text-gray-400">{c.telefon}</p>
                        </td>
                        <td className="px-4 py-3.5 hidden md:table-cell">
                          <p className="text-sm text-gray-600">{c.ilce || '-'}</p>
                        </td>
                        <td className="px-4 py-3.5 hidden lg:table-cell text-center">
                          <span className="text-sm text-gray-600 font-medium">{c.cihaz_sayisi}</span>
                        </td>
                        <td className="px-4 py-3.5 hidden sm:table-cell">
                          <p className="text-sm text-gray-600">
                            {sonBakimText}
                            {c.bir_sonraki_bakim_tarihi && (
                              <span className="text-xs text-gray-400 block">Sonraki: {sonrakiBakimText}</span>
                            )}
                          </p>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${d.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`}></span>
                            {d.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 hidden xl:table-cell text-right">
                          <p className="text-sm font-semibold text-emerald-600">
                            {c.toplam_harcama > 0 ? `${c.toplam_harcama.toLocaleString('tr-TR')} TL` : '-'}
                          </p>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1 justify-end">
                            <a href={`https://wa.me/${telefonWhatsApp(c.telefon)}`} target="_blank" rel="noopener noreferrer"
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-emerald-500 cursor-pointer" title="WhatsApp">
                              <i className="ri-whatsapp-line text-sm"></i>
                            </a>
                            <button onClick={() => setDetailCustomer(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-sky-50 text-sky-600 cursor-pointer" title="Detay">
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                            <button onClick={() => { setEditCustomer(c); setModalOpen(true); clearMessages(); }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer" title="Düzenle">
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button onClick={() => setDeleteId(c.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 cursor-pointer" title="Sil">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3">
              <p className="text-xs text-gray-400">
                {totalCount} müşteriden {(page - 1) * 15 + 1}–{Math.min(page * 15, totalCount)} arası gösteriliyor
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-sm">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap
                      ${p === page ? 'bg-sky-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-sm">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <CustomerModal open={modalOpen} onClose={() => { setModalOpen(false); setEditCustomer(null); }} onSave={handleSave} editData={editCustomer} saving={saving} />
      <CustomerDetailModal customer={detailCustomer} onClose={() => setDetailCustomer(null)} onRefresh={refetch} />

      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800 text-center">Müşteri Silinsin mi?</h3>
            <p className="text-sm text-gray-500 text-center mt-1">Bu işlem geri alınamaz. Müşteri pasif duruma alınacaktır.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap disabled:opacity-60">
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}